import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import Search from "antd/es/input/Search";
import { Image, Button, Popconfirm, message, Table } from "antd";
import { EyeOutlined, EditOutlined, DeleteOutlined, UserOutlined } from "@ant-design/icons";

import { getClients, deleteClient } from "../../api/client";
import { getFileUrl } from "../../api/file";
import { UserRole } from "../../lib/constants";

import PageHeader from "../../components/shared/PageHeader";
import BlueButton from "../../components/shared/BlueButton";

export default function ClientsView({ user }) {
    const [isLoading, setIsLoading] = useState(false);
    const [clients, setClients] = useState(null);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState(null);
    const perPage = 10;

    const columns = [
        {
            title: "SN",
            dataIndex: "id",
            key: "id",
            render: (text, record, index) => index + 1,
        },
        {
            title: "Image",
            dataIndex: "image",
            key: "image",
            render: (text, record) => {
                if (!record.avatar) return <div className="bg-orange-300 w-[48px] h-[48px] flex items-center justify-center rounded-full "><UserOutlined className="text-black" /></div>;;
                const url = getFileUrl(record.avatar)
                return < Image src={url} style={{ width: "48px", height: "48px", objectFit: "cover" }} className="rounded-full" />
            },
        },
        {
            title: "Client",
            dataIndex: "name",
            key: "name",
        },
        {
            title: "Trading Name",
            dataIndex: "tradingName",
            key: "tradingName",
            render: (text) => <p>{text || "N/A"}</p>
        },
        {
            title: "Address",
            dataIndex: "address",
            key: "address",
            render: (text, record) => {
                if (!record.address) return <p>N/A</p>;
                return <p>{record.address.name}</p>
            }
        },
        {
            title: "Actions",
            key: "action",
            render: (text, record) => (
                <div className="flex gap-2">
                    <Link to={`/clients/${record.id}`}>
                        <Button type="text" icon={<EyeOutlined />} />
                    </Link>
                    {user?.role !== UserRole.AccountManager && (
                        <>
                            <Link to={`/clients/form/${record.id}`}>
                                <Button type="text" icon={<EditOutlined />} />
                            </Link>
                            <Popconfirm
                                title="Delete the client"
                                description="Are you sure to delete this client?"
                                onConfirm={async () => await deleteClient(record.id).then(async () => {
                                    message.success("Client removed successfully");
                                    await fetchClients({ page, perPage, q: searchQuery });
                                })}
                                okText="Yes"
                                cancelText="No"
                            >
                                <Button type="text" icon={<DeleteOutlined />} danger />
                            </Popconfirm>
                        </>
                    )}
                </div>
            )
        }
    ]

    const fetchClients = useCallback(async ({ page, q, perPage }) => {
        try {
            setIsLoading(true);
            const { total, results } = await getClients({
                page, perPage, q
            });
            setTotal(total);
            setClients(results);
            setIsLoading(false);
        } catch (error) {
            console.error(error);
            message.error("Something went wrong");
            setIsLoading(false);
        }
    }, [])

    useEffect(() => {
        fetchClients({ page, perPage, q: searchQuery });
    }, [fetchClients, page, perPage, searchQuery])

    return (
        <main className="min-h-full flex flex-col space-y-6">
            <PageHeader title="Clients" home="App" route="/" />

            <div className="flex-grow bg-white ml-6 p-6 flex flex-col space-y-6">
                <div className="flex justify-between">
                    {user?.role !== UserRole.AccountManager && (
                        <Link to="/clients/form">
                            <BlueButton type="primary" >Add New Client</BlueButton>
                        </Link>
                    )}
                    <Search
                        placeholder="Search Client"
                        style={{
                            width: 200,
                        }}
                        allowClear
                        onSearch={(q) => setSearchQuery(q)}
                    />
                </div>


                <section>
                    <Table
                        dataSource={clients}
                        columns={columns}
                        loading={isLoading}
                        pagination={{ total, pageSize: perPage, current: page, onChange: (page) => setPage(page) }}
                    />
                </section>


            </div>
        </main>
    )
}
